import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import PageLayout from '../../components/base/PageLayout';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import useBrokerOAuthStore from '../../stores/useBrokerOAuthStore';
import AccountSelection from '../../components/broker/AccountSelection';

const BrokerCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuthContext();
  const { createBrokerLoginNotification, createBrokerErrorNotification } = useNotifications();
  const { getBrokerConnecting, clearBrokerOAuthData } = useBrokerOAuthStore();

  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');
  const [details, setDetails] = useState('');
  const [brokerData, setBrokerData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const processingRef = useRef(false);

  // ตรวจจับ broker type
  const brokerType = getBrokerConnecting() ||
    (document.referrer.includes('tradier.com') ? 'tradier' :
     document.referrer.includes('tastytrade.com') ? 'tastytrade' : 'unknown');

  // รอให้ auth โหลดเสร็จ
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setStatus('error');
      setMessage('Authentication required');
      setDetails('Please log in and try connecting your broker again.');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    handleOAuthCallback();
  }, [authLoading, user]);

  // หลัก function สำหรับจัดการ OAuth callback
  const handleOAuthCallback = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // ตรวจสอบ error จาก OAuth
      if (error) {
        setStatus('error');
        setMessage('Broker authentication failed');
        setDetails(searchParams.get('error_description') || error);
        clearBrokerOAuthData(brokerType);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid callback parameters');
        clearBrokerOAuthData(brokerType);
        return;
      }

      setMessage('Exchanging authorization code...');

      // ยิงไปหา backend เพื่อ exchange token
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net'
        : 'http://localhost:5001/quantum-flo-auto-algo-d3c2b/us-central1';

      const response = await fetch(`${baseUrl}/auth/exchange-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          broker: brokerType,
          code,
          state
        })
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Token exchange failed');
      }

      // เก็บข้อมูล broker และ accounts
      setBrokerData(result.data);

      // ส่ง raw accounts ไปให้ AccountSelection ทำ format เอง
      console.log('🔍 Raw accounts from backend:', result.accounts);
      setAccounts(result.accounts || []);

      // แสดงหน้าเลือกบัญชี
      setStatus('account_selection');
      setMessage('Select Trading Account');
      setDetails('Please select which trading account you want to connect:');

      // Clear OAuth state
      clearBrokerOAuthData(brokerType);

    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage('Connection failed');
      setDetails(error.message || 'Failed to connect broker account');
      clearBrokerOAuthData(brokerType);
    } finally {
      processingRef.current = false;
    }
  };

  // จัดการเมื่อผู้ใช้เลือกบัญชี
  const handleAccountSelected = async (selectedAccountId) => {
    try {
      setMessage('Saving account selection...');

      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net'
        : 'http://localhost:5001/quantum-flo-auto-algo-d3c2b/us-central1';

      const response = await fetch(`${baseUrl}/auth/select-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          broker: brokerType,
          userId: user.uid,
          accountId: selectedAccountId
        })
      });

      if (!response.ok) {
        throw new Error(`Account selection failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Account selection failed');
      }

      // สร้าง notification
      try {
        await createBrokerLoginNotification(brokerType, {
          accountId: selectedAccountId,
          accountName: selectedAccountId
        });
      } catch (err) {
        console.warn('Notification failed:', err);
      }

      // แสดงความสำเร็จ
      setStatus('success');
      setMessage('✅ Broker connected successfully!');
      setDetails(`Your ${brokerType} account has been connected.`);

      // ไปหน้า broker integrations
      setTimeout(() => {
        navigate('/broker-integrations');
      }, 2000);

    } catch (error) {
      console.error('Account selection error:', error);
      setStatus('error');
      setMessage('Failed to save account selection');
      setDetails(error.message || 'Please try again');
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <FiLoader className="w-12 h-12 text-blue-500 animate-spin" />;
      case 'success':
        return <FiCheckCircle className="w-12 h-12 text-green-500" />;
      case 'error':
        return <FiXCircle className="w-12 h-12 text-red-500" />;
      case 'account_selection':
        return (
          <div className="w-16 h-16 text-blue-500">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
        );
      default:
        return <FiLoader className="w-12 h-12 text-blue-500 animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-400';
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      case 'account_selection':
        return 'text-yellow-400';
      default:
        return 'text-blue-400';
    }
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-gray-900 rounded-lg p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <FiLoader className="w-12 h-12 text-blue-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-4 text-blue-500">
                Checking authentication...
              </h1>
              <p className="text-gray-400">
                Please wait while we verify your session
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-gray-900 rounded-lg p-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              {getStatusIcon()}
            </div>

            <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
              {message}
            </h1>

            {details && (
              <p className="text-gray-400 mb-6">
                {details}
              </p>
            )}

            {status === 'processing' && (
              <div className="space-y-2">
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
                <p className="text-sm text-gray-500">
                  This may take a few moments...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
                  <p className="text-green-400 text-sm">
                    ✓ Authorization successful<br />
                    ✓ Account verified<br />
                    ✓ Integration saved<br />
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  Redirecting to integrations page...
                </p>
              </div>
            )}

            {/* Account Selection */}
            {status === 'account_selection' && accounts.length > 0 && (
              <div className="mb-8">
                <AccountSelection
                  broker={brokerType}
                  accounts={accounts}
                  onAccountSelected={handleAccountSelected}
                  onCancel={() => {
                    setTimeout(() => navigate('/broker-integrations'), 500);
                  }}
                />
              </div>
            )}

            {/* Error Actions */}
            {status === 'error' && (
              <div className="space-y-4">
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      clearBrokerOAuthData(brokerType);
                      navigate('/broker-integrations');
                    }}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/broker-integrations')}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Return to Broker Integrations
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default BrokerCallbackPage;